import { useMutation } from "@tanstack/react-query";

import { useSupabaseClient } from "@/hooks/use-supabase-client";
import { scanBlockAdvisory, type AdvisoryIssue } from "@/lib/content-scan";
import type { Block, Page } from "@/types/database";

export interface PublishScanBlockingIssue {
  blockId: string;
  blockType: string;
  matchedNames: string[];
}

export interface PublishScanAdvisoryIssue {
  blockId: string;
  blockType: string;
  issues: AdvisoryIssue[];
}

export interface PublishScanResult {
  blocking: PublishScanBlockingIssue[];
  advisory: PublishScanAdvisoryIssue[];
}

interface ScanRpcRow {
  block_id: string;
  block_type: string;
  matched_names: string[] | null;
}

// Runs when a page's own Published switch (Page Settings) is flipped on —
// publishing is a page-by-page action now, there's no separate site-level
// publish button. scan_page_for_youth_pii is the read-only preflight
// backed by the same server-side logic that actually enforces the
// invariant via triggers (see
// supabase/migrations/20260714163000_publish_pii_scan.sql) — this dialog
// exists to show a friendly report before the user hits a trigger's raw
// exception, not to be the enforcement mechanism itself.
export function usePublishScan() {
  const getClient = useSupabaseClient();

  const scan = useMutation({
    mutationFn: async (page: Page): Promise<PublishScanResult> => {
      const supabase = await getClient();

      const [{ data: scanRows, error: scanError }, { data: pageBlocks, error: blocksError }] = await Promise.all([
        supabase.rpc("scan_page_for_youth_pii" as any, { target_page_id: page.id }),
        supabase.from("blocks").select("*").eq("page_id", page.id),
      ]);
      if (scanError) throw scanError;
      if (blocksError) throw blocksError;

      const blocking: PublishScanBlockingIssue[] = ((scanRows ?? []) as unknown as ScanRpcRow[]).map((row) => ({
        blockId: row.block_id,
        blockType: row.block_type,
        matchedNames: row.matched_names ?? [],
      }));

      const advisory: PublishScanAdvisoryIssue[] = [];
      for (const block of (pageBlocks ?? []) as Block[]) {
        const issues = scanBlockAdvisory(block);
        if (issues.length > 0) {
          advisory.push({ blockId: block.id, blockType: block.block_type, issues });
        }
      }

      return { blocking, advisory };
    },
  });

  return {
    runScan: (page: Page) => scan.mutateAsync(page),
    scanning: scan.isPending,
    error: scan.error instanceof Error ? scan.error.message : null,
  };
}
